export interface StatsLabels {
  customers?: string;
  products?: string;
  support?: string;
  satisfaction?: string;
}

export const DEFAULT_STATS_LABELS: Required<StatsLabels> = {
  customers: 'Mutlu Müşteriler',
  products: 'Ürün Çeşitliliği',
  support: 'Müşteri Desteği',
  satisfaction: 'Memnuniyet Oranı',
};

export type StoryImagePosition = 'left' | 'right';
export type StoryImageShape = 'circle' | 'rounded';

export interface StorySectionExtras {
  enabled?: boolean;
  statsEnabled?: boolean;
  statsBackgroundColor?: string;
  statsTextColor?: string;
  statsCellBorderEnabled?: boolean;
  statsLabels?: StatsLabels;
  /** Hikaye metninin yanında görsel */
  imageEnabled?: boolean;
  imageUrl?: string;
  imagePosition?: StoryImagePosition;
  imageShape?: StoryImageShape;
}

export interface SectionVisibilityExtras {
  enabled?: boolean;
}

export type AboutValuesTemplate = 'cards' | 'quality-assurance' | 'text-columns';

export interface AboutValueItem {
  id?: string;
  title?: string;
  description?: string;
  icon?: string;
  color?: string;
}

export interface ValuesSectionExtras {
  enabled?: boolean;
  template?: AboutValuesTemplate;
  eyebrow?: string;
  checklist?: string[];
  headingColor?: string;
  headingFontSize?: number;
  accentColor?: string;
  /** Panelden kaldırılan değerler dahil, vitrinde görünen sıra */
  items?: AboutValueItem[];
}

export const DEFAULT_VALUES_EYEBROW = 'KALİTE GÜVENCESİ';
export const DEFAULT_VALUES_HEADING_COLOR = '#1b4332';
export const DEFAULT_VALUES_ACCENT_COLOR = '#b8956a';
export const DEFAULT_VALUES_CHECKLIST = [
  'Kalıntı ve pestisit içermeyen temiz hammadde garantisi',
  'T.C. Tarım ve Orman Bakanlığı onaylı organik işletme belgesi',
  'Çift aşamalı mikrobiyolojik ve kimyasal laboratuvar taraması',
];

export const DEFAULT_VALUES_HEADING_FONT_SIZE = 44;

export const normalizeAboutValuesTemplate = (value: unknown): AboutValuesTemplate => {
  if (value === 'quality-assurance' || value === 'text-columns') return value;
  return 'cards';
};

export type AboutCtaAnimationType = 'none' | 'fade-up' | 'fade-in' | 'scale';
export type CorporateCtaPage = 'about' | 'services';
export type CorporateCtaButtonAction = 'url' | 'scroll-to-top' | 'contact-page';
export type AboutCtaTemplate = 'classic' | 'dark-banner';

export const normalizeAboutCtaTemplate = (value: unknown): AboutCtaTemplate =>
  value === 'dark-banner' ? 'dark-banner' : 'classic';

export interface AboutCtaSectionExtras {
  enabled?: boolean;
  template?: AboutCtaTemplate;
  eyebrow?: string;
  title?: string;
  description?: string;
  backgroundColor?: string;
  headingColor?: string;
  accentColor?: string;
  headingFontSize?: number;
  descriptionFontSize?: number;
  descriptionPaddingX?: number;
  descriptionPaddingY?: number;
  descriptionBorderColor?: string;
  primaryButtonEnabled?: boolean;
  primaryButtonText?: string;
  primaryButtonUrl?: string;
  primaryButtonAction?: CorporateCtaButtonAction;
  primaryButtonFontSize?: number;
  secondaryButtonEnabled?: boolean;
  secondaryButtonText?: string;
  secondaryButtonUrl?: string;
  secondaryButtonAction?: CorporateCtaButtonAction;
  secondaryButtonFontSize?: number;
  buttonRadius?: number;
  showContact?: boolean;
  phoneLabel?: string;
  phone?: string;
  email?: string;
  animationType?: AboutCtaAnimationType;
  animationDurationMs?: number;
  sectionPaddingY?: number;
  displayPages?: CorporateCtaPage[];
}

export const DEFAULT_ABOUT_CTA: Required<AboutCtaSectionExtras> = {
  enabled: true,
  template: 'classic',
  eyebrow: 'STRATEJİK İŞ BİRLİĞİ',
  title: 'Mutfaklarınız İçin Güvenilir Bir Çözüm Ortağı Arıyorsanız, Tanışalım.',
  description:
    'Kurumsal menü planlamalarınız, toplu hammadde alımlarınız ve özel gramaj talepleriniz için HoReCa satış danışmanlarımız size özel teklif ve sevkiyat planı hazırlamaya hazır.',
  backgroundColor: '#ffffff',
  headingColor: '#1b4332',
  accentColor: '#c45c26',
  headingFontSize: 36,
  descriptionFontSize: 16,
  descriptionPaddingX: 28,
  descriptionPaddingY: 22,
  descriptionBorderColor: '#d4cce3',
  primaryButtonEnabled: true,
  primaryButtonText: 'B2B Teklif Formu Doldurun',
  primaryButtonUrl: '/contact',
  primaryButtonAction: 'contact-page',
  primaryButtonFontSize: 15,
  secondaryButtonEnabled: true,
  secondaryButtonText: 'Kataloğu İndirin (PDF)',
  secondaryButtonUrl: '',
  secondaryButtonAction: 'url',
  secondaryButtonFontSize: 15,
  buttonRadius: 8,
  showContact: true,
  phoneLabel: 'Doğrudan HoReCa Hattı',
  phone: '+90 (224) 411 20 00',
  email: 'kurumsal@dogalgida.com.tr',
  animationType: 'fade-up',
  animationDurationMs: 800,
  sectionPaddingY: 112,
  displayPages: ['about'],
};

export const normalizeAboutCtaAnimation = (value: unknown): AboutCtaAnimationType => {
  if (value === 'none' || value === 'fade-in' || value === 'scale' || value === 'fade-up') {
    return value;
  }
  return 'fade-up';
};

const resolveCtaNumber = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

export const normalizeCtaButtonAction = (value: unknown): CorporateCtaButtonAction => {
  if (value === 'scroll-to-top' || value === 'contact-page' || value === 'url') return value;
  return 'url';
};

export const normalizeCtaDisplayPages = (
  value: unknown,
  fallback: CorporateCtaPage[] = DEFAULT_ABOUT_CTA.displayPages
): CorporateCtaPage[] => {
  if (!Array.isArray(value)) return [...fallback];
  const pages = value.filter((page): page is CorporateCtaPage => page === 'about' || page === 'services');
  return pages.length > 0 ? pages : [...fallback];
};

export const resolveAboutCtaSection = (
  extras?: AboutCtaSectionExtras | null,
  options?: { defaultDisplayPages?: CorporateCtaPage[] }
): Required<AboutCtaSectionExtras> => {
  const src = extras || {};
  const fallbackPages = options?.defaultDisplayPages || DEFAULT_ABOUT_CTA.displayPages;
  return {
    enabled: src.enabled !== false,
    template: normalizeAboutCtaTemplate(src.template),
    eyebrow: src.eyebrow ?? DEFAULT_ABOUT_CTA.eyebrow,
    title: src.title ?? DEFAULT_ABOUT_CTA.title,
    description: src.description ?? DEFAULT_ABOUT_CTA.description,
    backgroundColor: src.backgroundColor || DEFAULT_ABOUT_CTA.backgroundColor,
    headingColor: src.headingColor || DEFAULT_ABOUT_CTA.headingColor,
    accentColor: src.accentColor || DEFAULT_ABOUT_CTA.accentColor,
    headingFontSize: resolveCtaNumber(src.headingFontSize, DEFAULT_ABOUT_CTA.headingFontSize),
    descriptionFontSize: resolveCtaNumber(src.descriptionFontSize, DEFAULT_ABOUT_CTA.descriptionFontSize),
    descriptionPaddingX: resolveCtaNumber(src.descriptionPaddingX, DEFAULT_ABOUT_CTA.descriptionPaddingX),
    descriptionPaddingY: resolveCtaNumber(src.descriptionPaddingY, DEFAULT_ABOUT_CTA.descriptionPaddingY),
    descriptionBorderColor: src.descriptionBorderColor || DEFAULT_ABOUT_CTA.descriptionBorderColor,
    primaryButtonEnabled: src.primaryButtonEnabled !== false,
    primaryButtonText: src.primaryButtonText ?? DEFAULT_ABOUT_CTA.primaryButtonText,
    primaryButtonUrl: src.primaryButtonUrl ?? DEFAULT_ABOUT_CTA.primaryButtonUrl,
    primaryButtonAction: normalizeCtaButtonAction(
      src.primaryButtonAction ?? DEFAULT_ABOUT_CTA.primaryButtonAction
    ),
    primaryButtonFontSize: resolveCtaNumber(src.primaryButtonFontSize, DEFAULT_ABOUT_CTA.primaryButtonFontSize),
    secondaryButtonEnabled: src.secondaryButtonEnabled !== false,
    secondaryButtonText: src.secondaryButtonText ?? DEFAULT_ABOUT_CTA.secondaryButtonText,
    secondaryButtonUrl: src.secondaryButtonUrl ?? DEFAULT_ABOUT_CTA.secondaryButtonUrl,
    secondaryButtonAction: normalizeCtaButtonAction(
      src.secondaryButtonAction ?? DEFAULT_ABOUT_CTA.secondaryButtonAction
    ),
    secondaryButtonFontSize: resolveCtaNumber(
      src.secondaryButtonFontSize,
      DEFAULT_ABOUT_CTA.secondaryButtonFontSize
    ),
    buttonRadius: resolveCtaNumber(src.buttonRadius, DEFAULT_ABOUT_CTA.buttonRadius),
    showContact: src.showContact !== false,
    phoneLabel: src.phoneLabel ?? DEFAULT_ABOUT_CTA.phoneLabel,
    phone: src.phone ?? DEFAULT_ABOUT_CTA.phone,
    email: src.email ?? DEFAULT_ABOUT_CTA.email,
    animationType: normalizeAboutCtaAnimation(src.animationType ?? DEFAULT_ABOUT_CTA.animationType),
    animationDurationMs: resolveCtaNumber(src.animationDurationMs, DEFAULT_ABOUT_CTA.animationDurationMs),
    sectionPaddingY: resolveCtaNumber(src.sectionPaddingY, DEFAULT_ABOUT_CTA.sectionPaddingY),
    displayPages: normalizeCtaDisplayPages(src.displayPages, fallbackPages),
  };
};

export const ctaShowsOnPage = (
  settings: Pick<AboutCtaSectionExtras, 'enabled' | 'displayPages'>,
  page: CorporateCtaPage
): boolean => settings.enabled !== false && (settings.displayPages || []).includes(page);

export const mergeServiceCtaSettings = (
  source?: Record<string, unknown> | null,
  extras?: AboutCtaSectionExtras | null
): Required<AboutCtaSectionExtras> => {
  const src = source || {};
  const pickString = (value: unknown): string | undefined =>
    typeof value === 'string' ? value : undefined;
  const pickBool = (value: unknown): boolean | undefined =>
    typeof value === 'boolean' ? value : undefined;
  const action = (value: unknown): CorporateCtaButtonAction | undefined =>
    value === 'scroll-to-top' || value === 'contact-page' || value === 'url' ? value : undefined;
  const urlFromAction = (ctaAction: unknown, url?: string): string | undefined => {
    if (url != null && url !== '') return url;
    if (ctaAction === 'contact-page') return '/contact';
    return url;
  };

  return resolveAboutCtaSection(
    {
      ...extras,
      enabled: pickBool(src.ctaEnabled) ?? extras?.enabled,
      title: extras?.title ?? pickString(src.ctaTitle),
      description: extras?.description ?? pickString(src.ctaDescription),
      backgroundColor: extras?.backgroundColor || pickString(src.ctaBackgroundColor),
      primaryButtonEnabled: pickBool(src.ctaPrimaryButtonEnabled) ?? extras?.primaryButtonEnabled,
      primaryButtonText: extras?.primaryButtonText ?? pickString(src.ctaPrimaryButtonText),
      primaryButtonAction: extras?.primaryButtonAction ?? action(src.ctaPrimaryButtonAction),
      primaryButtonUrl: urlFromAction(src.ctaPrimaryButtonAction, extras?.primaryButtonUrl),
      secondaryButtonEnabled: pickBool(src.ctaSecondaryButtonEnabled) ?? extras?.secondaryButtonEnabled,
      secondaryButtonText: extras?.secondaryButtonText ?? pickString(src.ctaSecondaryButtonText),
      secondaryButtonAction: extras?.secondaryButtonAction ?? action(src.ctaSecondaryButtonAction),
      secondaryButtonUrl: urlFromAction(src.ctaSecondaryButtonAction, extras?.secondaryButtonUrl),
    },
    { defaultDisplayPages: ['services'] }
  );
};

export interface SectionSettingsPayload {
  storySection?: StorySectionExtras;
  valuesSection?: ValuesSectionExtras;
  whyChooseSection?: SectionVisibilityExtras;
  ctaSection?: AboutCtaSectionExtras;
  productsSection?: SectionVisibilityExtras;
  solutionsSection?: SectionVisibilityExtras;
}

export const CORPORATE_SIDEBAR_PAGE_KEYS = ['about', 'services', 'products', 'references'] as const;
export type CorporateSidebarPageKey = (typeof CORPORATE_SIDEBAR_PAGE_KEYS)[number];
export type CorporatePageNavPlacement = 'navbar' | 'panel' | 'both';

export interface CorporateSidebarSettings {
  /** JSON içinde saklanır — API sütunu yoksa vitrin buradan okur */
  layoutMode?: 'below_hero' | 'below_navbar';
  /**
   * Destek talepleri vitrin görünürlüğü.
   * Production'da ayrı kolon olmadan menü JSON'ında saklanır.
   * false: vitrinde Destek Taleplerim gizlenir.
   */
  supportTicketsEnabled?: boolean;
  /**
   * Sayfa bazlı yerleşim: üst menü (navbar), yan menü (panel) veya ikisi (both).
   * Yoksa corporateNavDisplayMode varsayılanı kullanılır.
   */
  pageNavPlacement?: Partial<Record<CorporateSidebarPageKey, CorporatePageNavPlacement>>;
  /**
   * true: ilgili kurumsal sayfada yan paneli gizle (içerik tam genişlik).
   * Varsayılan: panel tüm sayfalarda açık.
   */
  hideSidebarOn?: Partial<Record<CorporateSidebarPageKey, boolean>>;
  backgroundColor?: string;
  borderColor?: string;
  labelColor?: string;
  itemColor?: string;
  itemHoverBackgroundColor?: string;
  itemHoverColor?: string;
  activeBackgroundColor?: string;
  activeTextColor?: string;
  subitemColor?: string;
  subitemActiveColor?: string;
  subitemActiveBackgroundColor?: string;
}

export const DEFAULT_STATS_BACKGROUND_COLOR = 'linear-gradient(135deg, #3b82f6 0%, #4f46e5 100%)';
export const DEFAULT_STATS_TEXT_COLOR = '#ffffff';

export const TEXT_COLOR_PRESETS = [
  { label: 'Beyaz', value: '#ffffff' },
  { label: 'Koyu', value: '#0f172a' },
  { label: 'Antrasit', value: '#1e293b' },
  { label: 'Gri', value: '#334155' },
  { label: 'Mavi', value: '#1d4ed8' },
] as const;

export const COLOR_PALETTE_PRESETS = [
  { label: 'Mavi', value: 'linear-gradient(135deg, #3b82f6 0%, #4f46e5 100%)' },
  { label: 'İndigo', value: 'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)' },
  { label: 'Mor', value: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)' },
  { label: 'Yeşil', value: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' },
  { label: 'Turuncu', value: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' },
  { label: 'Kırmızı', value: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' },
  { label: 'Koyu', value: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)' },
  { label: 'Açık Gri', value: '#f1f5f9' },
  { label: 'Beyaz', value: '#ffffff' },
] as const;

export const DEFAULT_CORPORATE_SIDEBAR_SETTINGS: Required<CorporateSidebarSettings> = {
  layoutMode: 'below_hero',
  supportTicketsEnabled: true,
  pageNavPlacement: {},
  hideSidebarOn: {},
  backgroundColor: '#ffffff',
  borderColor: '#e5e7eb',
  labelColor: '#2563eb',
  itemColor: '#374151',
  itemHoverBackgroundColor: '#f9fafb',
  itemHoverColor: '#111827',
  activeBackgroundColor: '#eff6ff',
  activeTextColor: '#1d4ed8',
  subitemColor: '#6b7280',
  subitemActiveColor: '#1d4ed8',
  subitemActiveBackgroundColor: '#eff6ff',
};

export const parseSectionSettingsJson = (raw: unknown): SectionSettingsPayload => {
  if (!raw) return {};
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return parsed && typeof parsed === 'object' ? (parsed as SectionSettingsPayload) : {};
  } catch {
    return {};
  }
};

/** API kaydından sectionSettingsJson / SectionSettingsJson / sectionSettings okur */
export const resolveSectionSettingsFromSource = (
  source: unknown
): SectionSettingsPayload => {
  if (!source || typeof source !== 'object') return {};
  const record = source as Record<string, unknown>;
  if (record.sectionSettings && typeof record.sectionSettings === 'object') {
    return record.sectionSettings as SectionSettingsPayload;
  }
  const raw = record.sectionSettingsJson ?? record.SectionSettingsJson;
  return parseSectionSettingsJson(raw);
};

const parseHideSidebarOn = (
  raw: unknown
): Partial<Record<CorporateSidebarPageKey, boolean>> => {
  if (!raw || typeof raw !== 'object') return {};
  const rec = raw as Record<string, unknown>;
  const result: Partial<Record<CorporateSidebarPageKey, boolean>> = {};
  for (const key of CORPORATE_SIDEBAR_PAGE_KEYS) {
    if (rec[key] === true) result[key] = true;
  }
  return result;
};

const parsePageNavPlacement = (
  raw: unknown
): Partial<Record<CorporateSidebarPageKey, CorporatePageNavPlacement>> => {
  if (!raw || typeof raw !== 'object') return {};
  const rec = raw as Record<string, unknown>;
  const result: Partial<Record<CorporateSidebarPageKey, CorporatePageNavPlacement>> = {};
  for (const key of CORPORATE_SIDEBAR_PAGE_KEYS) {
    const value = rec[key];
    if (value === 'navbar' || value === 'panel' || value === 'both') {
      result[key] = value;
    }
  }
  return result;
};

export const parseCorporateSidebarSettingsJson = (raw: unknown): CorporateSidebarSettings => {
  if (!raw) return { ...DEFAULT_CORPORATE_SIDEBAR_SETTINGS };
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!parsed || typeof parsed !== 'object') {
      return { ...DEFAULT_CORPORATE_SIDEBAR_SETTINGS };
    }
    const { layoutMode, hideSidebarOn, pageNavPlacement, ...rest } = parsed as CorporateSidebarSettings;
    return {
      ...DEFAULT_CORPORATE_SIDEBAR_SETTINGS,
      ...(rest as CorporateSidebarSettings),
      hideSidebarOn: parseHideSidebarOn(hideSidebarOn),
      pageNavPlacement: parsePageNavPlacement(pageNavPlacement),
      ...(layoutMode === 'below_navbar' || layoutMode === 'below_hero' ? { layoutMode } : {}),
    };
  } catch {
    return { ...DEFAULT_CORPORATE_SIDEBAR_SETTINGS };
  }
};

type CorporateNavMenuSettings = {
  corporateNavDisplayMode?: string;
  corporateSidebarSettings?: CorporateSidebarSettings;
  corporateSidebarSettingsJson?: string;
} | null | undefined;

export const getCorporatePageNavPlacement = (
  menuSettings: CorporateNavMenuSettings,
  page: CorporateSidebarPageKey
): CorporatePageNavPlacement => {
  const fromJson = parseCorporateSidebarSettingsJson(menuSettings?.corporateSidebarSettingsJson);
  const explicit =
    menuSettings?.corporateSidebarSettings?.pageNavPlacement?.[page] ??
    fromJson.pageNavPlacement?.[page];
  if (explicit === 'navbar' || explicit === 'panel' || explicit === 'both') {
    return explicit;
  }
  return menuSettings?.corporateNavDisplayMode === 'panel' ? 'panel' : 'navbar';
};

export const shouldShowCorporateNavbarGroup = (
  menuSettings: CorporateNavMenuSettings,
  page: CorporateSidebarPageKey
): boolean => {
  const placement = getCorporatePageNavPlacement(menuSettings, page);
  return placement === 'navbar' || placement === 'both';
};

export const anyCorporatePageUsesSidebar = (menuSettings: CorporateNavMenuSettings): boolean =>
  CORPORATE_SIDEBAR_PAGE_KEYS.some((page) => {
    const placement = getCorporatePageNavPlacement(menuSettings, page);
    return placement === 'panel' || placement === 'both';
  });

export const shouldShowCorporatePageSidebar = (
  menuSettings: CorporateNavMenuSettings,
  page: CorporateSidebarPageKey
): boolean => {
  const placement = getCorporatePageNavPlacement(menuSettings, page);
  if (placement === 'navbar') return false;
  const fromJson = parseCorporateSidebarSettingsJson(menuSettings?.corporateSidebarSettingsJson);
  const hideSidebarOn = {
    ...(fromJson.hideSidebarOn || {}),
    ...(menuSettings?.corporateSidebarSettings?.hideSidebarOn || {}),
  };
  return hideSidebarOn[page] !== true;
};

export const serializeSectionSettingsJson = (payload: SectionSettingsPayload): string =>
  JSON.stringify(payload ?? {});

export const serializeCorporateSidebarSettingsJson = (
  payload: CorporateSidebarSettings,
  layoutMode?: 'below_hero' | 'below_navbar'
): string =>
  JSON.stringify({
    ...DEFAULT_CORPORATE_SIDEBAR_SETTINGS,
    ...payload,
    ...(layoutMode ? { layoutMode } : {}),
  });

export const resolveCorporateSidebarLayoutMode = (
  menuSettings?: {
    corporateSidebarLayoutMode?: string;
    corporateSidebarSettings?: CorporateSidebarSettings;
    corporateSidebarSettingsJson?: string;
  } | null
): 'below_hero' | 'below_navbar' => {
  if (menuSettings?.corporateSidebarLayoutMode === 'below_navbar') {
    return 'below_navbar';
  }
  const fromObject = menuSettings?.corporateSidebarSettings?.layoutMode;
  if (fromObject === 'below_navbar') return 'below_navbar';
  const parsed = parseCorporateSidebarSettingsJson(menuSettings?.corporateSidebarSettingsJson);
  return parsed.layoutMode === 'below_navbar' ? 'below_navbar' : 'below_hero';
};

export const isSectionEnabled = (
  sectionSettings: SectionSettingsPayload,
  sectionKey: keyof SectionSettingsPayload,
  defaultEnabled = true
): boolean => {
  const section = sectionSettings[sectionKey];
  if (!section || section.enabled === undefined) return defaultEnabled;
  return section.enabled !== false;
};
